import CardContainer from "@/components/Card/CardContainer";
import { useUserContext } from "@/context/UserContext";
import { QUOTA_TYPES } from "@/modules/Quotas/quota.constant";
import { Container } from "@mui/material";
import * as React from "react";
import { useState } from "react";
import { useAgencyOrAdvertiserById } from "../Company/company.hook";
import { IQuota, IQuotaUsage } from "../Quotas/quota.interface";
import AccountDetail from "./Detail";
import AccountForm from "./Form";

export type AccountPage = "Detail" | "Form";

export interface ProcessedQuotaData {
  limit: number | string;
  total: number;
  usage: number;
  rollover: number;
  isUnlimited: boolean;
}

export interface AccountUserData {
  username: string;
  email: string;
  companyName: string;
}

export interface AccountQuotaData {
  campaignImpressions: ProcessedQuotaData | null;
  advertisersLimit: string | null;
  linkedinProfilesLimit: string | null;
}

export default function AccountInfo() {
  const { user } = useUserContext();
  const { data: company, isLoading } = useAgencyOrAdvertiserById({
    id: user?.companyId,
  });
  const [activePage, setActivePage] = useState<AccountPage>("Detail");

  const userData: AccountUserData = React.useMemo(() => {
    return {
      username: user?.username ?? "",
      email: user?.email ?? "",
      companyName: company?.name ?? "",
    };
  }, [user, company]);

  const quotaData: AccountQuotaData = React.useMemo(() => {
    if (!company?.Quotas) {
      return {
        campaignImpressions: null,
        advertisersLimit: null,
        linkedinProfilesLimit: null,
      };
    }

    // Find quotas by type
    const impressionsQuota = company.Quotas.find(
      (q: IQuota) => q.type === QUOTA_TYPES.CAMPAIGN_IMPRESSIONS
    );
    const advertisersQuota = company.Quotas.find(
      (q: IQuota) => q.type === QUOTA_TYPES.ADVERTISERS
    );
    const linkedinQuota = company.Quotas.find(
      (q: IQuota) => q.type === QUOTA_TYPES.LINKEDIN_PROFILES_RESOLVED
    );

    // Find quota usages
    const impressionsUsage = company.QuotaUsages?.find(
      (u: IQuotaUsage) => u.quotaId === impressionsQuota?.id && u.isActive
    );

    // Process campaign impressions with usage data
    let campaignImpressions: ProcessedQuotaData | null = null;
    if (impressionsQuota && impressionsUsage) {
      const isUnlimited = impressionsQuota.limit === "Infinity";
      const limit = isUnlimited ? Infinity : Number(impressionsQuota.limit);
      const total = impressionsUsage.total ?? limit;
      const usage = impressionsUsage.usage ?? 0;
      const rollover = isUnlimited ? 0 : Math.max(0, total - limit);

      campaignImpressions = {
        limit: impressionsQuota.limit,
        total,
        usage,
        rollover,
        isUnlimited,
      };
    }

    return {
      campaignImpressions,
      advertisersLimit: getQuotaDisplayValue(advertisersQuota),
      linkedinProfilesLimit: getQuotaDisplayValue(linkedinQuota),
    };
  }, [company]);

  return (
    <Container maxWidth="md">
      <CardContainer sx={{ px: 4, py: 3, gap: 6 }}>
        {activePage === "Detail" ? (
          <AccountDetail
            userData={userData}
            quotaData={quotaData}
            setActivePage={setActivePage}
            isLoading={isLoading}
          />
        ) : (
          <AccountForm setActivePage={setActivePage} />
        )}
      </CardContainer>
    </Container>
  );
}

function getQuotaDisplayValue(quota: IQuota | undefined): string | null {
  if (!quota?.limit) return null;
  if (quota.limit === "Infinity") return "∞";
  if (quota.interval === "fixed") return String(quota.limit);
  return `${quota.limit}/${quota.interval}`;
}
